import { useEffect, useMemo, useState } from 'react'
import { BASE_CHAIN_ID_NUMBER } from '@mprolab/sdk'
import { ethers } from 'ethers'
import { Contract } from 'ethers'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { ERC721_MIN_ABI } from './abi'
import {
  useMgaLightNodesContractDetails,
  useRegularNodesContractDetails,
  useMproTokenContractAddress
} from 'state/global/hooks'
import type { MarketplaceCollectionRef } from '@mprolab/sdk'

export interface MarketplaceContext {
  marketplaceAddress: string | null
  chainId: number
  collections: MarketplaceCollectionRef[]
  /** MPRO ERC-20 address on Base (used for allowance + balance reads). */
  mproAddress: string | null
  /** Block the marketplace proxy was created in — the floor for log scans. */
  deployedAtBlock: number
  /** Decides how much MPRO is burned on a transfer. */
  masterDistributorAddress: string
}

/**
 * The deployed marketplace proxy on Base. Hardcoded on purpose: it is a public
 * address, it is permanent (the proxy address survives every upgrade), and
 * putting it here means a fresh deploy of the web app works without a
 * dashboard setting somebody has to remember. VITE_MARKETPLACE_ADDRESS still
 * overrides it, which is what a testnet or a fork needs.
 */
const DEPLOYED_MARKETPLACE = '0xc5d4d137349ced313918919ab48501c14af9e547'

/** Proxy creation block, so log scans never start at genesis. */
const DEPLOYED_AT_BLOCK = 50655587

/**
 * MPRO's master distributor on Base, which decides how much of a transfer is
 * burned. The token exposes no getter for it and the app keeps no address for
 * it, so it is named here — public, permanent, and overridable by env for a
 * testnet.
 *
 * It is read per seller: `getBurnAmount(from, amount)` returns 0 for a
 * whitelisted address, so a seller sees their own burn rather than an average.
 */
const MASTER_DISTRIBUTOR = '0x568cd600a92b26f848804e4005822f7d28753632'

/**
 * Resolves the marketplace contract address (from VITE_MARKETPLACE_ADDRESS)
 * and the addresses of the licence-NFT collections currently allowed for
 * trading. The allowlist is read from the existing global state that
 * `useGlobalSettings` populates from `/api/config/contracts`, so no
 * separate backend call is required.
 */
export function useMarketplaceContext(): MarketplaceContext {
  const mgaLight = useMgaLightNodesContractDetails() // ggNODE
  const regular = useRegularNodesContractDetails() // iNODE
  const mproContracts = useMproTokenContractAddress()

  return useMemo(() => {
    const chainId = BASE_CHAIN_ID_NUMBER
    const onBase = <T extends { chainId: number; isActive: boolean; address: string }>(list: T[]): T | undefined =>
      list.find((c) => c.chainId === chainId && c.isActive)

    const collections: MarketplaceCollectionRef[] = []
    const inode = onBase(regular)
    if (inode) {
      collections.push({
        nft: inode.address.toLowerCase(),
        label: 'iNODE',
        licenseType: 'mga_inode'
      })
    }
    const ggnode = onBase(mgaLight)
    if (ggnode) {
      collections.push({
        nft: ggnode.address.toLowerCase(),
        label: 'ggNODE',
        licenseType: 'mga_light'
      })
    }
    /*
     * MGA is deliberately absent, and the reason is on chain: its NFT
     * contract answers `transferEnabled() == false`, so no MGA token can
     * move at all. The marketplace's own allowlist still carries it, which
     * is exactly what made this look tradable — but an allowlist entry only
     * says the marketplace would accept the listing, never that the NFT can
     * be delivered. A listing or offer made against it settles into a revert.
     *
     * Everything downstream keys off this list: the type filter, both
     * creation forms, and the Sell / Sell-to-offer / Transfer actions on a
     * licence row, all of which gate on the collection being present. So
     * leaving MGA out removes a third market type that does not exist,
     * rather than disabling it in five places.
     *
     * Put it back the day the operator calls `setTransferEnabled(true)` on
     * the collection — not before, and not on the strength of the allowlist.
     */

    const mproOnBase = onBase(mproContracts)
    const configured = (import.meta.env.VITE_MARKETPLACE_ADDRESS as string | undefined)?.toLowerCase()
    const deployedAtBlock = Number(
      (import.meta.env.VITE_MARKETPLACE_DEPLOY_BLOCK as string | undefined) ?? DEPLOYED_AT_BLOCK
    )

    return {
      marketplaceAddress: configured || DEPLOYED_MARKETPLACE,
      chainId,
      collections,
      mproAddress: mproOnBase?.address.toLowerCase() ?? null,
      deployedAtBlock,
      masterDistributorAddress:
        (import.meta.env.VITE_MASTER_DISTRIBUTOR_ADDRESS as string | undefined)?.toLowerCase() || MASTER_DISTRIBUTOR
    }
  }, [mgaLight, regular, mproContracts])
}
